import { createContext, useContext, useEffect, useState } from 'react';

import type { DxfSplitResult } from '@frontend/api';

import { DxfMappingActionsRoot } from './mapping/DxfMappingActionsRoot';
import type { DxfMappingQuoteToSplitTo } from './mapping/mappingActions';
import { createDxfViewerSession } from './session/createDxfViewerSession';
import type { DxfViewerSession } from './session/types';

const DxfViewerContext = createContext<DxfViewerSession | null>(null);
DxfViewerContext.displayName = 'DxfViewerContext';

/**
 * Owns the page's DXF viewer session, and the mapping's entity actions over it.
 *
 * Consumers read controller lenses through `useReadableStore` (or helpers like
 * `useDxfLoad` / `useSessionModel`), so they re-render only on the slice they
 * asked for — never on pan or zoom.
 *
 * Mount once per viewer page, alongside `WebViewerContextProvider` and inside the
 * page's viewer contract roots (edit locks and access), which the mapping actions
 * read. Doing so on a page whose file is not a DXF costs nothing: a session holds
 * no DOM and no WebGL until `attachSurface` is called, and reports
 * `load.status: 'idle'` until then.
 *
 * `onMappingSaved` lets the page refresh what a mapping changes: saving one
 * re-measures the part, and so its dimensions and pricing. `quoteToSplitTo` is
 * where a split's line items land: the quote the page acts in, or unavailable
 * with the reason when the page knows it; pages that act in no quote leave it
 * out. `onSplitStarted` runs once a split task has been created and
 * `onSplitFinished` once it ends, for the page's own side effects.
 */
export const DxfViewerProvider = ({
  children,
  onMappingSaved,
  quoteToSplitTo,
  onSplitStarted,
  onSplitFinished,
}: {
  children: React.ReactNode;
  onMappingSaved?: () => void;
  quoteToSplitTo?: DxfMappingQuoteToSplitTo;
  onSplitStarted?: () => void;
  onSplitFinished?: (result: DxfSplitResult) => void;
}) => {
  const [session] = useState(createDxfViewerSession);

  // Cleanup the session when the component unmounts.
  useEffect(() => () => session.dispose(), [session]);

  return (
    <DxfViewerContext.Provider value={session}>
      <DxfMappingActionsRoot
        session={session}
        onMappingSaved={onMappingSaved}
        quoteToSplitTo={quoteToSplitTo}
        onSplitStarted={onSplitStarted}
        onSplitFinished={onSplitFinished}
      >
        {children}
      </DxfMappingActionsRoot>
    </DxfViewerContext.Provider>
  );
};
DxfViewerProvider.displayName = 'DxfViewerProvider';

export const useDxfViewerSession = (): DxfViewerSession => {
  const session = useContext(DxfViewerContext);
  if (!session) {
    throw new Error(
      'useDxfViewerSession must be used within a DxfViewerProvider',
    );
  }
  return session;
};
